import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ChevronDown, ChevronRight } from 'lucide-react';
import BridgeWaterfall from './BridgeWaterfall';
import BoardPackRateVolume from './BoardPackRateVolume';
import EvidenceList from './EvidenceList';
import SignalMixBar from './SignalMixBar';
import {
  buildExecutiveSignificantItems,
  buildScreenSummaryRows,
  formatAccounting,
  lineKey,
  synthesizeSummaryWaterfall
} from './boardPackHelpers';
import {
  basisTabLabel,
  bucketFindings,
  findingsForScreen,
  formatMillions,
  linesForScreen,
  screenById,
  signalNoiseCounts
} from './boardScreenHelpers';

const varianceTone = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return '';
  return n < 0 ? 'bp-negative' : 'bp-positive';
};

const EMPTY_OBJECT = {};

const bridgeKeyOf = (line = {}) =>
  `${line.scopeLevel || 'line'}-${line.planningEntity || ''}-${line.glGroup || ''}`;

// Per-screen copy for the four-box canvas. Loans/Deposits emphasis on the balance
// sheet stays; the other screens summarize by their major GL-group buckets.
const CANVAS_COPY = {
  bs: {
    heading: 'Balance-sheet executive review',
    matrixTitle: 'Balance-sheet highlights',
    matrixHint: 'Loans and deposits lead; other classes roll up beneath — not a raw GL dump.',
    waterfallLabel: 'B/S total',
    rollForward: true
  },
  nim: {
    heading: 'Net interest income executive review',
    matrixTitle: 'Interest income and expense',
    matrixHint: 'Aggregated by GL group across business units, not every planning-entity line.',
    waterfallLabel: 'Net interest income'
  },
  nir: {
    heading: 'Non-interest revenue executive review',
    matrixTitle: 'Fee and other revenue',
    matrixHint: 'Aggregated by GL group across business units, not every planning-entity line.',
    waterfallLabel: 'Total non-interest revenue'
  },
  nie: {
    heading: 'Non-interest expense executive review',
    matrixTitle: 'Expense categories',
    matrixHint: 'Aggregated by GL group across business units, not every planning-entity line.',
    waterfallLabel: 'Total non-interest expense'
  },
  provision: {
    heading: 'Provision and allowance executive review',
    matrixTitle: 'Provision and tax',
    matrixHint: 'Aggregated by GL group across business units, not every planning-entity line.',
    waterfallLabel: 'Provision movement'
  }
};

const canvasCopy = (screenId) => CANVAS_COPY[screenId] || {
  heading: 'Executive review',
  matrixTitle: 'Summary',
  matrixHint: 'Aggregated by GL group across business units.',
  waterfallLabel: 'Total movement'
};

function ComponentMiniTable({ bridge }) {
  const components = Array.isArray(bridge?.components) ? bridge.components : [];
  const rows = components.map((component, index) => ({
    key: component.id || `component-${index}`,
    label: component.label || component.type || `Component ${index + 1}`,
    amount: Number(component.amount ?? 0)
  }));
  if (bridge?.residual !== undefined && Math.abs(Number(bridge.residual)) > 0.0001) {
    rows.push({ key: 'residual', label: 'Residual', amount: Number(bridge.residual) });
  }
  if (!rows.length) return <p className="bp-muted">No bridge components.</p>;
  return (
    <table className="bp-table bp-component-table">
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            <td>{row.label}</td>
            <td className="bp-mono bp-amount">{formatAccounting(row.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SummaryMatrix({ rows, basis, selectedId, onSelect, title, hint }) {
  return (
    <div className="bp-exec-table-card">
      <div className="bp-exec-panel-head">
        <div>
          <div className="ace-eyebrow">Summary matrix</div>
          <strong>{title} vs {basisTabLabel(basis)}</strong>
        </div>
        <small className="bp-muted">{hint}</small>
      </div>
      <table className="bp-table bp-exec-summary-table bp-bs-summary-table">
        <thead>
          <tr>
            <th>Line</th>
            <th className="bp-amount">Actual</th>
            <th className="bp-amount">{basisTabLabel(basis)}</th>
            <th className="bp-amount">Variance $</th>
            <th className="bp-amount">Variance %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className={`bp-exec-summary-row${selectedId === row.id ? ' bp-exec-summary-row--active' : ''}`}
              onClick={() => onSelect(row.id)}
            >
              <td>
                <span className="bp-exec-line-label">
                  <strong>{row.label}</strong>
                  <small>{row.section}</small>
                </span>
              </td>
              <td className="bp-mono bp-amount">{formatAccounting(row.actual)}</td>
              <td className="bp-mono bp-amount">{formatAccounting(row.reference)}</td>
              <td className={`bp-mono bp-amount ${varianceTone(row.variance)}`}>
                {formatAccounting(row.variance)}
              </td>
              <td className={`bp-mono bp-amount ${varianceTone(row.variance)}`}>
                {row.variancePct}
              </td>
            </tr>
          ))}
          {!rows.length && (
            <tr><td colSpan={5} className="bp-muted">No summary rows returned for this screen.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function HierarchyDriver({ driver, lines, bridges, loadingKeys, fetchBridge }) {
  const [expanded, setExpanded] = useState(false);
  const line = lines.find((candidate) => lineKey(candidate) === driver.key);
  const bridgeKey = line ? bridgeKeyOf(line) : '';
  const bridge = bridgeKey ? bridges[bridgeKey] : null;

  const toggle = () => {
    if (!line) return;
    setExpanded((value) => !value);
    fetchBridge(line);
  };

  return (
    <li className="bp-bs-driver">
      <button type="button" className="bp-bs-driver-trigger" onClick={toggle}>
        {expanded ? <ChevronDown size={13} aria-hidden /> : <ChevronRight size={13} aria-hidden />}
        <span>
          <strong>{driver.glGroup || 'GL line'}</strong>
          <small>{driver.planningEntity || 'company'}{driver.hasNarrative ? ' · narrated' : ''}</small>
        </span>
        <span className={`bp-mono bp-amount ${varianceTone(driver.variance)}`}>
          {formatAccounting(driver.variance)}
        </span>
      </button>
      {expanded && (
        <div className="bp-bs-driver-detail">
          {loadingKeys.has(bridgeKey) && <small className="bp-muted">Loading bridge…</small>}
          {bridge?.error && <p className="bp-negative">{bridge.error}</p>}
          {bridge && !bridge.error && <ComponentMiniTable bridge={bridge} />}
          {!bridge && !loadingKeys.has(bridgeKey) && <small className="bp-muted">Bridge not loaded yet.</small>}
        </div>
      )}
    </li>
  );
}

const findingChipTone = (finding) => {
  const label = String(finding.classificationLabel || '').toLowerCase();
  if (label.includes('open') || label.includes('confirmed')) return 'signal';
  if (label.includes('noise') || label.includes('suppress')) return 'muted';
  return 'known';
};

// Top anomalous and — per requirement — especially dispositioned changes in the bucket,
// with the finding brief and the disposition note, plus the existing workbench drill-in.
function DispositionFindings({ findings = [], onOpenWorkbench }) {
  const ranked = (findings || []).filter(
    (finding) => Math.abs(Number(finding.dollarImpact || 0)) > 0 || finding.brief
  );
  if (!ranked.length) {
    return <small className="bp-muted">No bottom-up findings in this bucket.</small>;
  }
  return (
    <ul className="bp-mix-finding-list">
      {ranked.slice(0, 4).map((finding) => (
        <li key={finding.id} className="bp-mix-finding">
          <div className="bp-mix-finding-head">
            <span className={`bp-mix-chip bp-mix-chip--${findingChipTone(finding)}`}>
              {finding.classificationLabel || 'Finding'}
            </span>
            <span className="bp-mix-finding-scope">
              <strong>{finding.glGroup || 'GL group'}</strong>
              <small>{finding.planningEntity || 'company'}</small>
            </span>
            <span className={`bp-mono bp-amount ${varianceTone(finding.dollarImpact)}`}>
              {formatMillions(finding.dollarImpact)}
            </span>
          </div>
          {finding.brief && <p className="bp-mix-finding-brief">{finding.brief}</p>}
          {finding.dispositionNote && (
            <p className="bp-mix-finding-note">
              Disposition{finding.verdict ? ` (${String(finding.verdict).replace(/_/g, ' ')})` : ''}: {finding.dispositionNote}
            </p>
          )}
          {onOpenWorkbench && (
            <button
              type="button"
              className="bp-inline-link"
              onClick={() => onOpenWorkbench(finding)}
            >
              Open finding {String(finding.id).slice(0, 6)} →
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function DrilldownItem({ item, active, onSelect, lines, bridges, loadingKeys, fetchBridge, onOpenWorkbench }) {
  return (
    <article className={`bp-exec-item${active ? ' bp-exec-item--active' : ''}`}>
      <button type="button" className="bp-exec-item-trigger" onClick={() => onSelect(item.id)}>
        {active ? <ChevronDown size={13} aria-hidden /> : <ChevronRight size={13} aria-hidden />}
        <span>
          <strong>{item.label}</strong>
          <small>{item.section || 'bucket'}</small>
        </span>
        <span className={`bp-mono bp-amount ${varianceTone(item.variance)}`}>
          {formatAccounting(item.variance)}
        </span>
      </button>
      <div className="bp-mix-inline">
        <SignalMixBar mix={item.signalMix} compact={!active} />
      </div>
      {active && (
        <div className="bp-exec-item-body">
          <DispositionFindings findings={item.findings} onOpenWorkbench={onOpenWorkbench} />
          {item.drivers.length > 0 && (
            <ul className="bp-bs-driver-list">
              {item.drivers.map((driver) => (
                <HierarchyDriver
                  key={driver.key}
                  driver={driver}
                  lines={lines}
                  bridges={bridges}
                  loadingKeys={loadingKeys}
                  fetchBridge={fetchBridge}
                />
              ))}
            </ul>
          )}
          {!item.drivers.length && <small className="bp-muted">No underlying drivers for this bucket.</small>}
        </div>
      )}
    </article>
  );
}

function DrilldownList({ items, selectedId, onSelect, lines, bridges, loadingKeys, fetchBridge, onOpenWorkbench }) {
  return (
    <div className="bp-bs-hierarchy-list">
      {items.map((item) => (
        <DrilldownItem
          key={item.id}
          item={item}
          active={selectedId === item.id}
          onSelect={onSelect}
          lines={lines}
          bridges={bridges}
          loadingKeys={loadingKeys}
          fetchBridge={fetchBridge}
          onOpenWorkbench={onOpenWorkbench}
        />
      ))}
      {!items.length && <p className="bp-muted">No drilldown buckets available for this screen.</p>}
    </div>
  );
}

// Four-box executive-review canvas shared by BP_02–BP_06: summary matrix (top-left),
// summary waterfall (bottom-left), row-linked narrative (top-right), and a bottom-up
// anomaly/disposition drilldown with a 100% signal mix (bottom-right).
function ExecutiveReviewCanvas({
  screen,
  screenId,
  lines,
  execRows,
  basis,
  bridges,
  loadingKeys,
  fetchBridge,
  findingsById,
  expectationsByKey,
  evidence,
  findings,
  findingsLoading,
  onOpenWorkbench,
  onSelectBridge,
  onReturnToExecutive,
  extraPanel
}) {
  const copy = canvasCopy(screenId);
  const summaryRows = useMemo(() => buildScreenSummaryRows(screenId, lines), [screenId, lines]);
  const evidenceFindings = evidence?.findings || EMPTY_OBJECT;
  const mergedFindingsById = useMemo(
    () => ({ ...evidenceFindings, ...(findingsById || {}) }),
    [evidenceFindings, findingsById]
  );
  const items = useMemo(
    () => buildExecutiveSignificantItems({
      board: { execRows },
      lines,
      summaryRows,
      findingsById: mergedFindingsById,
      evidence,
      basis,
      limit: summaryRows.length
    }),
    [execRows, lines, summaryRows, mergedFindingsById, evidence, basis]
  );
  const waterfall = useMemo(
    () => synthesizeSummaryWaterfall(summaryRows, copy.waterfallLabel),
    [summaryRows, copy.waterfallLabel]
  );
  const [selectedId, setSelectedId] = useState('');

  useEffect(() => {
    if (!items.length) {
      setSelectedId('');
      return;
    }
    setSelectedId((current) => (items.some((item) => item.id === current) ? current : items[0].id));
  }, [items]);

  const selectedItem = items.find((item) => item.id === selectedId) || items[0] || null;
  const selectedRowId = selectedItem?.id || summaryRows[0]?.id || '';
  const guard = selectedItem?.guardPass;

  return (
    <section className="ace-panel bp-section">
      <div className="bp-exec-title-row">
        <div>
          <div className="ace-eyebrow">BP_{screen.ord} <span aria-hidden>●</span> {screen.label}</div>
          <h3>{copy.heading}</h3>
        </div>
        {onReturnToExecutive && (
          <button
            type="button"
            className="bp-inline-link bp-return-to-exec"
            onClick={onReturnToExecutive}
          >
            <ArrowLeft size={13} aria-hidden /> Back to executive review
          </button>
        )}
      </div>

      <div className="bp-exec-canvas bp-bs-canvas">
        <div className="bp-exec-column bp-exec-column--left">
          <div className="bp-exec-cell bp-exec-cell--matrix">
            <SummaryMatrix
              rows={summaryRows}
              basis={basis}
              selectedId={selectedRowId}
              onSelect={setSelectedId}
              title={copy.matrixTitle}
              hint={copy.matrixHint}
            />
          </div>

          <section className="bp-exec-bridge-panel bp-exec-cell bp-exec-cell--waterfall">
            <div className="bp-exec-panel-head">
              <div>
                <div className="ace-eyebrow">Summary waterfall</div>
                <strong>{copy.matrixTitle} walk vs {basisTabLabel(basis)}</strong>
              </div>
              <small className="bp-muted">Aggregated by review bucket, not planning-entity GL detail.</small>
            </div>
            <BridgeWaterfall bridge={waterfall} height={230} />
            <div className="bp-waterfall-legend">
              {waterfall.components.map((component) => (
                <button
                  key={component.id}
                  type="button"
                  className="bp-inline-link"
                  onClick={() => setSelectedId(component.id)}
                >
                  {component.label}
                </button>
              ))}
            </div>
          </section>

          {extraPanel}
        </div>

        <div className="bp-exec-column bp-exec-column--right">
          <section className="bp-exec-selected bp-exec-cell bp-exec-cell--narrative">
            <div className="bp-exec-panel-head">
              <div>
                <div className="ace-eyebrow">Summary narrative</div>
                <strong>{selectedItem?.label || copy.matrixTitle}</strong>
              </div>
              <div className="bp-exec-narrative-meta">
                {typeof guard === 'boolean' && (
                  <span className={`bp-guard-badge bp-guard-badge--${guard ? 'pass' : 'fail'}`}>
                    {guard ? 'Guard pass' : 'Guard fail'}
                  </span>
                )}
                {selectedItem && (
                  <span className={`bp-mono bp-amount ${varianceTone(selectedItem.variance)}`}>
                    {formatAccounting(selectedItem.variance)}
                  </span>
                )}
              </div>
            </div>
            <p className="bp-commentary-body">
              {selectedItem?.body || 'Select a summary row to review its variance narrative and evidence.'}
            </p>
            <EvidenceList
              citations={selectedItem?.citations}
              evidence={evidence}
              findingsById={mergedFindingsById}
              expectationsByKey={expectationsByKey}
              onSelectFinding={onOpenWorkbench}
              onSelectBridge={onSelectBridge}
            />
          </section>

          <section className="bp-exec-significant bp-exec-cell bp-exec-cell--drilldown">
            <div className="bp-exec-panel-head">
              <div>
                <div className="ace-eyebrow">Signal mix and drilldown</div>
                <strong>Anomalous vs dispositioned vs ordinary — by bucket</strong>
              </div>
              <small className="bp-muted">
                {findingsLoading ? 'Loading monitor findings…' : signalNoiseCounts(bucketFindings(findings || []))}
              </small>
            </div>
            <DrilldownList
              items={items}
              selectedId={selectedRowId}
              onSelect={setSelectedId}
              lines={lines}
              bridges={bridges}
              loadingKeys={loadingKeys}
              fetchBridge={fetchBridge}
              onOpenWorkbench={onOpenWorkbench}
            />
          </section>
        </div>
      </div>
    </section>
  );
}

export default function BoardScreen({
  screenId,
  lines,
  execRows,
  basis,
  bridges,
  loadingKeys,
  fetchBridge,
  findingsById,
  expectationsByKey,
  evidence = {},
  findings,
  findingsLoading,
  focusLineKey = '',
  onFocusConsumed,
  onOpenWorkbench,
  onSelectBridge,
  onReturnToExecutive
}) {
  const screen = screenById(screenId);
  const screenLines = useMemo(() => linesForScreen(screenId, lines), [screenId, lines]);
  const screenFindings = useMemo(
    () => findingsForScreen(screenId, findings, lines),
    [screenId, findings, lines]
  );

  // Eagerly warm the bridge cache for the most material line so the first drilldown
  // expansion is instant, and honor a cross-tab focus deep-link when one arrives.
  useEffect(() => {
    if (focusLineKey) {
      const target = screenLines.find((line) => lineKey(line) === focusLineKey);
      if (target) {
        fetchBridge(target);
        onFocusConsumed?.();
        return;
      }
    }
    const material = [...screenLines].sort(
      (a, b) => Math.abs(Number(b.variance || 0)) - Math.abs(Number(a.variance || 0))
    )[0];
    if (material) fetchBridge(material);
  }, [screenId, screenLines, fetchBridge, focusLineKey, onFocusConsumed]);

  const rateVolumePanel =
    screenId === 'nim' ? (
      <div className="bp-exec-cell bp-exec-cell--ratevolume">
        <BoardPackRateVolume
          lineBridgePairs={screenLines
            .map((line) => ({ line, bridge: bridges[bridgeKeyOf(line)] }))
            .filter((pair) => pair.bridge && !pair.bridge.error)}
        />
      </div>
    ) : null;

  return (
    <ExecutiveReviewCanvas
      screen={screen}
      screenId={screenId}
      lines={screenLines}
      execRows={execRows}
      basis={basis}
      bridges={bridges}
      loadingKeys={loadingKeys}
      fetchBridge={fetchBridge}
      findingsById={findingsById}
      expectationsByKey={expectationsByKey}
      evidence={evidence}
      findings={screenFindings}
      findingsLoading={findingsLoading}
      onOpenWorkbench={onOpenWorkbench}
      onSelectBridge={onSelectBridge}
      onReturnToExecutive={onReturnToExecutive}
      extraPanel={rateVolumePanel}
    />
  );
}
